import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Textarea } from '../components/ui/textarea';
import { ArrowLeft, Save, Eye, Loader2 } from 'lucide-react';
import { toast } from '../hooks/use-toast';
import { contentAPI } from '../services/api';

const AdminContentEditor = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('terms');
  const [termsContent, setTermsContent] = useState('');
  const [privacyContent, setPrivacyContent] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [notifyUsers, setNotifyUsers] = useState(true);
  const [termsLastUpdated, setTermsLastUpdated] = useState('');
  const [privacyLastUpdated, setPrivacyLastUpdated] = useState('');

  useEffect(() => {
    fetchContent();
  }, []);

  const fetchContent = async () => {
    setLoading(true);
    try {
      const [terms, privacy] = await Promise.all([
        contentAPI.getTerms(),
        contentAPI.getPrivacy()
      ]);
      setTermsContent(terms.content);
      setPrivacyContent(privacy.content);
      setTermsLastUpdated(terms.last_updated);
      setPrivacyLastUpdated(privacy.last_updated);
    } catch (error) {
      console.error('Error fetching content:', error);
      toast({
        title: 'Error',
        description: 'Failed to load content',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      if (activeTab === 'terms') {
        await contentAPI.updateTerms(termsContent, notifyUsers);
        toast({
          title: 'Saved!',
          description: notifyUsers
            ? 'Terms of Service updated - emailing all users now'
            : 'Terms of Service updated (no email sent)'
        });
      } else {
        await contentAPI.updatePrivacy(privacyContent, notifyUsers);
        toast({
          title: 'Saved!',
          description: notifyUsers
            ? 'Privacy Policy updated - emailing all users now'
            : 'Privacy Policy updated (no email sent)'
        });
      }
      fetchContent(); // Refresh to get new timestamp
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to save changes',
        variant: 'destructive'
      });
    } finally {
      setSaving(false);
    }
  };

  const handlePreview = () => {
    if (activeTab === 'terms') {
      window.open('/terms', '_blank');
    } else {
      window.open('/privacy', '_blank');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: 'linear-gradient(to bottom, #0F1D2B, #17283A)' }}>
        <Loader2 className="w-8 h-8 text-wheely-orange-light animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ background: 'linear-gradient(to bottom, #0F1D2B, #17283A)' }}>
      {/* Header */}
      <div className="border-b" style={{ background: 'linear-gradient(to right, #1E3448, #0F1D2B)', borderColor: 'rgba(225, 116, 49, 0.3)' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                onClick={() => navigate('/admin/dashboard')}
                className="text-wheely-orange-light hover:text-wheely-orange-light"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Dashboard
              </Button>
              <h1 className="text-2xl font-bold text-white">Content Editor</h1>
            </div>
            <div className="flex items-center gap-2">
              <label className="flex items-center gap-2 text-sm text-white/80 mr-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={notifyUsers}
                  onChange={(e) => setNotifyUsers(e.target.checked)}
                  className="accent-[#E17431]"
                />
                Email all users about this change
              </label>
              <Button
                onClick={handlePreview}
                variant="outline"
                style={{ borderColor: 'rgba(225, 116, 49, 0.3)', color: '#E17431' }}
                className="hover:bg-wheely-orange/20/10"
              >
                <Eye className="w-4 h-4 mr-2" />
                Preview
              </Button>
              <Button
                onClick={handleSave}
                disabled={saving}
                style={{ background: 'linear-gradient(to right, #E17431, #EB9868)' }}
                className="text-white"
              >
                {saving ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4 mr-2" />
                    Save Changes
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Card style={{ background: 'rgba(30, 58, 74, 0.8)', borderColor: 'rgba(225, 116, 49, 0.3)' }}>
          <CardHeader>
            <CardTitle className="text-white">Edit Legal Content</CardTitle>
            <CardDescription className="text-gray-300">
              Update Terms of Service and Privacy Policy. Content supports Markdown formatting.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList className="grid w-full grid-cols-2" style={{ background: 'rgba(15, 23, 42, 0.8)' }}>
                <TabsTrigger 
                  value="terms"
                  className="data-[state=active]:text-white"
                  style={{ background: activeTab === 'terms' ? 'linear-gradient(to right, #E17431, #EB9868)' : 'transparent' }}
                >
                  Terms of Service
                </TabsTrigger>
                <TabsTrigger 
                  value="privacy"
                  className="data-[state=active]:text-white"
                  style={{ background: activeTab === 'privacy' ? 'linear-gradient(to right, #E17431, #EB9868)' : 'transparent' }}
                >
                  Privacy Policy
                </TabsTrigger>
              </TabsList>

              <TabsContent value="terms" className="mt-6 space-y-4">
                <div className="flex justify-between items-center">
                  <p className="text-sm text-gray-400">
                    Last updated: {termsLastUpdated ? new Date(termsLastUpdated).toLocaleString() : 'Never'}
                  </p>
                </div>
                <Textarea
                  value={termsContent}
                  onChange={(e) => setTermsContent(e.target.value)}
                  placeholder="Enter Terms of Service content here..."
                  rows={25}
                  style={{ 
                    background: 'rgba(15, 23, 42, 0.8)', 
                    borderColor: 'rgba(225, 116, 49, 0.3)',
                    fontFamily: 'monospace'
                  }}
                  className="text-white resize-none"
                />
                <div className="bg-wheely-orange/20/10 border border-wheely-orange/30/30 rounded-lg p-4">
                  <p className="text-wheely-orange-light text-sm">
                    <strong>Tip:</strong> Use Markdown for formatting:
                  </p>
                  <ul className="text-sm text-gray-400 mt-2 ml-4 space-y-1">
                    <li>## Heading → <strong>Heading</strong></li>
                    <li>**bold** → <strong>bold text</strong></li>
                    <li>- Item → • Bullet point</li>
                  </ul>
                </div>
              </TabsContent>

              <TabsContent value="privacy" className="mt-6 space-y-4">
                <div className="flex justify-between items-center">
                  <p className="text-sm text-gray-400">
                    Last updated: {privacyLastUpdated ? new Date(privacyLastUpdated).toLocaleString() : 'Never'}
                  </p>
                </div>
                <Textarea
                  value={privacyContent}
                  onChange={(e) => setPrivacyContent(e.target.value)}
                  placeholder="Enter Privacy Policy content here..."
                  rows={25}
                  style={{ 
                    background: 'rgba(15, 23, 42, 0.8)', 
                    borderColor: 'rgba(225, 116, 49, 0.3)',
                    fontFamily: 'monospace'
                  }}
                  className="text-white resize-none"
                />
                <div className="bg-wheely-orange/20/10 border border-wheely-orange/30/30 rounded-lg p-4">
                  <p className="text-wheely-orange-light text-sm">
                    <strong>Tip:</strong> Use Markdown for formatting:
                  </p>
                  <ul className="text-sm text-gray-400 mt-2 ml-4 space-y-1">
                    <li>## Heading → <strong>Heading</strong></li>
                    <li>**bold** → <strong>bold text</strong></li>
                    <li>- Item → • Bullet point</li>
                  </ul>
                </div>
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AdminContentEditor;
